import { IncrementalSyncService } from '@/lib/services/incremental-sync';
import { performanceMonitor } from './performance-monitor';

export interface ChangeRecord {
  id: string;
  table: string;
  operation: 'create' | 'update' | 'delete';
  timestamp: number;
  data?: any;
  originalData?: any;
}

export interface SyncBatch {
  id: string;
  changes: ChangeRecord[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
}

class SyncTracker {
  private changes: Map<string, ChangeRecord> = new Map();
  private batches: SyncBatch[] = [];
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic sync
    this.startPeriodicSync();
  }

  // Track a change
  trackChange(change: Omit<ChangeRecord, 'timestamp'>) {
    const changeRecord: ChangeRecord = {
      ...change,
      timestamp: Date.now()
    };

    // Use composite key for deduplication
    const key = `${change.table}:${change.id}:${change.operation}`;
    
    // If it's an update, merge with existing change
    if (change.operation === 'update' && this.changes.has(key)) {
      const existing = this.changes.get(key)!;
      changeRecord.originalData = existing.originalData;
    }

    this.changes.set(key, changeRecord);
    console.log(`Tracked change: ${change.operation} on ${change.table}:${change.id}`);
  }

  // Get all pending changes
  getPendingChanges(): ChangeRecord[] {
    return Array.from(this.changes.values());
  }

  // Get changes for a specific table
  getChangesForTable(table: string): ChangeRecord[] {
    return Array.from(this.changes.values()).filter(change => change.table === table);
  }

  // Get changes for a specific record
  getChangesForRecord(table: string, id: string): ChangeRecord[] {
    return Array.from(this.changes.values()).filter(
      change => change.table === table && change.id === id
    );
  }

  // Mark changes as synced
  markChangesSynced(changeIds: string[]) {
    changeIds.forEach(id => {
      this.changes.delete(id);
    });
  }

  // Mark changes as failed
  markChangesFailed(changeIds: string[], error: string) {
    console.error('Sync failed for changes:', changeIds, error);
    // Could implement retry logic here
  }

  // Start periodic sync
  private startPeriodicSync() {
    this.syncInterval = setInterval(() => {
      this.syncPendingChanges();
    }, 5000); // Sync every 5 seconds
  }

  // Stop periodic sync
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Manual sync trigger
  async syncPendingChanges() {
    if (this.isSyncing || this.changes.size === 0) return;

    this.isSyncing = true;
    const batchId = `batch_${Date.now()}`;
    const changes = this.getPendingChanges();
    const startTime = Date.now();

    const batch: SyncBatch = {
      id: batchId,
      changes,
      timestamp: Date.now(),
      status: 'syncing'
    };

    this.batches.push(batch);

    try {
      console.log(`Starting sync batch ${batchId} with ${changes.length} changes`);
      
      // Group changes by table for efficient syncing
      const changesByTable = this.groupChangesByTable(changes);
      
      for (const [table, tableChanges] of changesByTable) {
        await this.syncTableChanges(table, tableChanges);
      }

      // Mark batch as completed
      batch.status = 'completed';
      this.markChangesSynced(changes.map(c => `${c.table}:${c.id}:${c.operation}`));
      
      const responseTime = Date.now() - startTime;
      const dataSize = JSON.stringify(changes).length;
      performanceMonitor.trackSubscription(`sync_batch_${batchId}`, dataSize, responseTime);
      
      console.log(`Sync batch ${batchId} completed successfully`);
    } catch (error) {
      console.error(`Sync batch ${batchId} failed:`, error);
      batch.status = 'failed';
      this.markChangesFailed(
        changes.map(c => `${c.table}:${c.id}:${c.operation}`),
        error as string
      );
      
      const responseTime = Date.now() - startTime;
      const dataSize = JSON.stringify(changes).length;
      performanceMonitor.trackSubscription(`sync_batch_${batchId}_error`, dataSize, responseTime);
    } finally {
      this.isSyncing = false;
    }
  }

  // Group changes by table
  private groupChangesByTable(changes: ChangeRecord[]): Map<string, ChangeRecord[]> {
    const grouped = new Map<string, ChangeRecord[]>();
    
    changes.forEach(change => {
      if (!grouped.has(change.table)) {
        grouped.set(change.table, []);
      }
      grouped.get(change.table)!.push(change);
    });

    return grouped;
  }

  // Sync changes for a specific table
  private async syncTableChanges(table: string, changes: ChangeRecord[]) {
    console.log(`Syncing ${changes.length} changes for table: ${table}`);
    
    // Use the incremental sync service
    const changesByTable = new Map<string, ChangeRecord[]>();
    changesByTable.set(table, changes);
    
    const result = await IncrementalSyncService.batchSync(changesByTable);
    
    if (!result.success) {
      throw new Error(`Sync failed for table ${table}: ${result.errors.join(', ')}`);
    }
    
    console.log(`Successfully synced ${result.syncedChanges.length} changes for table ${table}`);
  }

  // Get sync status
  getSyncStatus() {
    return {
      pendingChanges: this.changes.size,
      isSyncing: this.isSyncing,
      recentBatches: this.batches.slice(-5) // Last 5 batches
    };
  }

  // Clear all changes (useful for testing)
  clearChanges() {
    this.changes.clear();
  }

  // Clear failed consultation records
  clearFailedConsultations() {
    const failedConsultationKeys = Array.from(this.changes.keys()).filter(key => 
      key.startsWith('consultations:') && 
      this.changes.get(key)?.operation === 'create'
    );
    
    failedConsultationKeys.forEach(key => {
      this.changes.delete(key);
      console.log(`Cleared failed consultation record: ${key}`);
    });
    
    return failedConsultationKeys.length;
  }
}

// Export singleton instance
export const syncTracker = new SyncTracker();

// Export utility functions
export const trackChange = (change: Omit<ChangeRecord, 'timestamp'>) => {
  syncTracker.trackChange(change);
};

export const clearFailedConsultations = () => {
  return syncTracker.clearFailedConsultations();
};

export const getPendingChanges = () => syncTracker.getPendingChanges();
export const getSyncStatus = () => syncTracker.getSyncStatus(); 